/**
 * BuilderPage — /builder/:id
 *
 * The manual editing workspace entry point. It fetches the persisted builder from the
 * backend by id (the DB is the single source of truth), guards the load states, then
 * mounts the editor context + workspace shell. There is no frontend schema state beyond
 * the editor's working copy, which is initialized from — and saved back to — the
 * backend; refresh, new tabs, and restarts all reload the latest saved values.
 */
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useI18nNavigate } from '../hooks/useI18nNavigate'
import { useGoToReport } from '../hooks/useGoToReport'
import { siteBuilderApi } from '../api/client'
import { BuilderLoading, BuilderError, BuilderEmpty } from './BuilderStates'
import { BuilderEditorProvider } from './builder/BuilderEditorContext'
import { BuilderWorkspace } from './builder/BuilderWorkspace'

export function BuilderPage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation('builder')
  const navigate = useI18nNavigate()

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['builder', id],
    queryFn: () => siteBuilderApi.getBuilder(id ?? ''),
    enabled: !!id,
    retry: 1,
    staleTime: 0,
  })

  const goBack = useGoToReport(data?.businessId)

  if (!id) {
    return (
      <BuilderEmpty
        title={t('builder.emptyTitle', 'No website yet')}
        subtitle={t('builder.emptySubtitle', 'Generate a website from your business report to preview it here.')}
        actionLabel={t('cta.button', 'Improve your website')}
        onGenerate={() => navigate('/')}
      />
    )
  }
  if (isLoading) {
    return <BuilderLoading message={t('builder.building', 'Loading your website…')} />
  }
  if (isError || !data || data.status !== 'succeeded' || !data.schema) {
    return (
      <BuilderError
        message={data?.errorMessage || t('builder.error', 'We couldn’t load this website.')}
        onRetry={() => void refetch()}
        onBack={goBack}
        retryLabel={t('builder.retry', 'Try again')}
        backLabel={t('builder.back', 'Back')}
      />
    )
  }

  // A fresh editor per builder id — remounting on id change re-seeds the working copy.
  return (
    <BuilderEditorProvider key={data.id} builder={data} initialSchema={data.schema}>
      <BuilderWorkspace />
    </BuilderEditorProvider>
  )
}
